import {IconButton} from '@wandb/components';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import * as RunFeed from '../../util/runfeed';
import {EMPTY_CONFIG} from '../../util/runfeed';
import * as TableCols from '../../util/tablecols';
// FIXME: WBTable should be package-private
import type {WBTableColumn} from '../RunSelector/WBTable/types';
import {useAutoColsRuns} from './hooks/useAutoColsRuns';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {RunSelectorRunsPartialVars} from './types';

interface RunsAutoColsBaseProps {
  onClick(): void;
}

const RunsAutoColsBaseButton = ({onClick}: RunsAutoColsBaseProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const label = 'Show most useful columns';

  return (
    <IconButton
      aria-label={label}
      icon="magic-wand-star"
      variant="secondary"
      onPress={() => {
        onClick();
        window.analytics?.track('Autohide columns button clicked', {
          location: analyticsLocation,
        });
      }}
      tooltip={{children: label}}
    />
  );
};

const RunsAutoColsLoadingButton = (props: RunsAutoColsProps) => {
  const {query, tableSettings, setTableSettings, currentPage, columns} = props;

  const {isLoading, runs} = useAutoColsRuns(query);
  const once = useRef(false);

  useEffect(() => {
    if (!isLoading && tableSettings != null && !once.current) {
      once.current = true;
      const pageSize = tableSettings.pageSize ?? EMPTY_CONFIG.pageSize;
      const i = pageSize * (currentPage - 1);
      const rows = runs.slice(i, i + pageSize);
      setTableSettings(
        TableCols.deriveAutoColumns(tableSettings, rows, columns)
      );
    }
  }, [isLoading, runs, setTableSettings, tableSettings, currentPage, columns]);

  return (
    <IconButton
      icon="magic-wand-star"
      isPending
      aria-label="Show most useful columns"
      variant="secondary"
    />
  );
};

interface RunsAutoColsProps {
  query: RunSelectorRunsPartialVars;
  tableSettings?: RunFeed.Config;
  currentPage: number;
  columns: WBTableColumn[];

  setTableSettings(config: RunFeed.Config): void;
}

export const RunsAutoCols = ({
  query,
  tableSettings,
  setTableSettings,
  currentPage,
  columns,
}: RunsAutoColsProps) => {
  const [loading, setLoading] = useState(false);

  const setTableSettingsWrapped = useCallback(
    (config: RunFeed.Config) => {
      setTableSettings(config);
      setLoading(false);
      toast('Now showing recommended columns.');
    },
    [setTableSettings, setLoading]
  );

  const startLoad = useCallback(() => setLoading(true), [setLoading]);

  return loading ? (
    <RunsAutoColsLoadingButton
      query={query}
      tableSettings={tableSettings}
      setTableSettings={setTableSettingsWrapped}
      currentPage={currentPage}
      columns={columns}
    />
  ) : (
    <RunsAutoColsBaseButton onClick={startLoad} />
  );
};
